import { KeyValuePair } from './conversation.interface';

export enum VideoMessageState {
  // Generic states — usable by any async video pipeline
  Processing = 'processing',
  Sending = 'sending',
  Sent = 'sent',
  Failed = 'failed',
  Cancelled = 'cancelled',
  // MMS-specific states
  UploadInProgress = 'upload-in-progress',
  UploadRejected = 'upload-rejected',
  TranscodingFailed = 'transcoding-failed',
  OutputTooLarge = 'output-too-large',
  TwilioRejected = 'twilio-rejected',
}

export const VIDEO_STATE_METADATA_KEY = 'videoState';

export const VIDEO_ERROR_MESSAGE_KEYS: Partial<Record<VideoMessageState, string>> = {
  [VideoMessageState.Failed]: 'INBOX.VIDEO.ERROR.FAILED',
  [VideoMessageState.Cancelled]: 'INBOX.VIDEO.ERROR.CANCELLED',
  [VideoMessageState.UploadRejected]: 'INBOX.VIDEO.ERROR.UPLOAD_REJECTED',
  [VideoMessageState.TranscodingFailed]: 'INBOX.VIDEO.ERROR.TRANSCODING_FAILED',
  [VideoMessageState.OutputTooLarge]: 'INBOX.VIDEO.ERROR.OUTPUT_TOO_LARGE',
  [VideoMessageState.TwilioRejected]: 'INBOX.VIDEO.ERROR.TWILIO_REJECTED',
};

export function isVideoMessage(metadata?: KeyValuePair[]): boolean {
  return !!metadata?.some((m) => m.key === VIDEO_STATE_METADATA_KEY);
}

// Async-job-type discriminator stamped by the producing service (e.g. social-posts).
export const ASYNC_JOB_TYPE_METADATA_KEY = 'asyncJobType';
export const ASYNC_JOB_TYPE_AI_VIDEO_GENERATION = 'ai-video-generation';

// Per-state message keys for AI-generated videos, so status copy is translated in the UI
// rather than read from the (English-only) server-authored message body.
export const AI_VIDEO_GENERATION_MESSAGE_KEYS: Partial<Record<VideoMessageState, string>> = {
  [VideoMessageState.Processing]: 'INBOX.VIDEO.AI_GENERATION.PROCESSING',
  [VideoMessageState.Failed]: 'INBOX.VIDEO.AI_GENERATION.FAILED',
  [VideoMessageState.Cancelled]: 'INBOX.VIDEO.AI_GENERATION.CANCELLED',
};

export function isAiVideoGenerationMessage(metadata?: KeyValuePair[]): boolean {
  return !!metadata?.some(
    (m) => m.key === ASYNC_JOB_TYPE_METADATA_KEY && m.value === ASYNC_JOB_TYPE_AI_VIDEO_GENERATION,
  );
}

const KNOWN_STATES: Set<string> = new Set(Object.values(VideoMessageState));

export function getVideoState(metadata?: KeyValuePair[]): VideoMessageState | undefined {
  const entry = metadata?.find((m) => m.key === VIDEO_STATE_METADATA_KEY);
  if (!entry) return undefined;
  return KNOWN_STATES.has(entry.value) ? (entry.value as VideoMessageState) : undefined;
}
